import { Checkbox, Grid, makeStyles, Typography } from '@material-ui/core';
import clsx from 'clsx';
import Loading from 'components/Loading';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import i18n from 'i18n';
import PermissionCard from 'containers/Groups/PermissionCard';
import Alert from '@material-ui/lab/Alert';
import styles from '../../Template/styles';
import { ENTITY_NAME } from './Content';

const useStyles = makeStyles(styles);

interface Props {
  title: string;
  subtitle: string;
  isLoading?: boolean;
  permissions?: {
    loading: boolean;
    selected: string[];
    available: { name: string; selected: boolean }[];
  };
  pending: string[];
  setPending: (pending: string[]) => void;
  updatePermissions: (permissions: string[]) => void;
}

const ListPermissions = ({
  title,
  subtitle,
  permissions,
  pending,
  isLoading,
  setPending,
  updatePermissions,
}: Props) => {
  const classes = useStyles();
  const [checked, setChecked] = React.useState<string[]>([]);

  const permissionsNames: string[] = permissions?.available?.map(
    item => item.name
  ) || [''];

  const permissionList = [
    {
      key: 'partner',
      label: i18n.t('PARTNER_KEY'),
      values: [
        'list_partner_details',
        'list_partner_dashboard',
        'list_all_partners',
        'edit_partner_details',
        'delete_partner',
        'list_partner_servers',
        'start_partner_servers',
        'stop_partner_servers',
        'publish_new_image_partner_servers',
        'publish_image_partner_servers',
        'can_export_partner_summary_list',
        'can_block_actions_partner_servers',
        'can_unblock_actions_partner_servers',
        'list_partner_instances',
        'list_leaked_credentials',
        'reset_leaked_credentials_user_password',
        'list_leaked_credentials_clients',
      ].filter(item => permissionsNames.includes(item)),
    },
    {
      key: 'custom-frontend',
      label: i18n.t('GROUPS_PAGE.FRONTEND_CUSTOMIZATION_KEY'),
      values: [
        'list_user_theme_config',
        'create_user_theme_config',
        'edit_user_theme_config',
        'delete_user_theme_image',
        'publish_user_theme_config',
        'delete_user_theme_config',
      ].filter(item => permissionsNames.includes(item)),
    },
    {
      key: 'environment',
      label: i18n.t('ENVIRONMENTS_KEY'),
      values: ['create_new_environment', 'migrate_environment'].filter(item =>
        permissionsNames.includes(item)
      ),
    },
    {
      key: 'ldap',
      label: 'LDAP',
      values: [
        'can_test_ldap',
        'can_change_external_ad_password',
      ].filter(item => permissionsNames.includes(item)),
    },
  ].filter(item => item.values.length > 0);

  const handleCheck = {
    exists: (value: string) => checked.includes(value),
    toggle: (value: string) => {
      setPending(
        pending.includes(value)
          ? pending.filter(item => item !== value)
          : pending.concat(value)
      );

      const newCheckeds = checked.includes(value)
        ? checked.filter(item => item !== value)
        : [...checked, value];

      setChecked(newCheckeds);
      updatePermissions(newCheckeds);
    },
  };

  React.useEffect(() => {
    if (permissions?.selected) {
      setChecked(permissions.selected);
    }
  }, [permissions]);

  const allPermissionsAvailable = React.useMemo(() => permissionsNames || [], [
    permissionsNames,
  ]);

  const handleCheckAllExists = (values: string[]) =>
    values.reduce(
      (permissionsResponse, currentValue) =>
        permissionsResponse && checked.includes(currentValue),
      true
    );

  const handleToggleAll = (values: string[]) => {
    let newListPending = [...pending];
    values.forEach(value => {
      if (newListPending.includes(value)) {
        newListPending = newListPending.filter(item => item !== value);
      } else {
        newListPending = [...newListPending, value];
      }
    });
    setPending(newListPending);
    if (handleCheckAllExists(values)) {
      setChecked([]);
      updatePermissions([]);
    } else {
      setChecked(values);
      updatePermissions(values);
    }
  };

  return (
    <React.Fragment>
      {permissionList.length === 0 ? (
        <Grid container justify="center" className={classes.warning}>
          <Alert className={classes.alert} severity="warning" color="info">
            <Grid item>
              <Typography>{i18n.t('NO_PERMISSIONS_IN_GROUP_KEY')}</Typography>
            </Grid>
          </Alert>
        </Grid>
      ) : (
        <Grid
          container
          spacing={2}
          className={clsx({
            [classes.disableArea]: permissions?.loading,
          })}
        >
          <Grid item xs={12} style={{ margin: '32px 0 8px' }}>
            <Typography variant="h6">{title}</Typography>
            <Typography variant="body2">{subtitle}</Typography>
          </Grid>
          {(permissions?.loading && !permissionsNames) || isLoading ? (
            <Loading />
          ) : (
            <>
              <Grid container className={classes.superTitle}>
                <Checkbox
                  size="small"
                  checked={handleCheckAllExists(allPermissionsAvailable)}
                  onChange={() => {
                    handleToggleAll(allPermissionsAvailable);
                  }}
                  className={classes.checkIcon}
                />
                <Typography className={classes.titleText}>
                  {i18n.t('GROUPS_PAGE.SELECT_ALL_KEY')}
                </Typography>
              </Grid>
              <PermissionCard
                groupPermissions={permissionList}
                entityKey="partner"
                permissions={permissions}
                pending={pending}
                handleCheck={handleCheck}
                handleCheckAllExists={handleCheckAllExists}
                setPending={setPending}
                updatePermissions={updatePermissions}
                checked={checked}
                setChecked={setChecked}
              />
            </>
          )}
        </Grid>
      )}
    </React.Fragment>
  );
};

const mapStateToProps = ({
  permission: { permissions, isLoading },
}: ApplicationState) => ({
  permissions: permissions[ENTITY_NAME],
  isLoading,
});

export default connect(mapStateToProps)(ListPermissions);
